import { useMutation } from "@tanstack/react-query";
import _ from "lodash";
import { useEffect, useState } from "react";
import { Alert, Col, Container, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useTranslations } from "../../../hook/templates";
import { getTranslatableLanguages, languages, LanguageCode } from "../../../models/company";
import { Translation, TranslationsToUpdate } from "../../../models/templates";
import { useAppSelector } from "../../../store";
import TinyEditor from "../../../utils/TinyEditor";
import { queryClient } from "../../../utils/http/queryClient";
import { updateTranslations } from "../../../utils/http/templates";
import { stripOuterPFromTranslations } from "../../../utils/text";
import { getTranslationForKey } from "../../../utils/translations";

function TranslationsAllLanguages({
  allKeysToTranslate,
  specificKeysToTranslate,
}: {
  allKeysToTranslate: string[];
  specificKeysToTranslate: string[];
}) {
  const selectedPath = useAppSelector((state) => state.templates.selectedPath);
  const selectedVersion = useAppSelector((state) => state.templates.selectedVersion);
  const activeCompany = useAppSelector((state) => state.company.activeCompany);

  const translatableLanguages = (() => {
    const ids = getTranslatableLanguages(activeCompany?.info?.languages);
    if (!ids.length) return languages;
    const filtered = languages.filter((l) => ids.includes(l.id as LanguageCode));
    return filtered.length ? filtered : languages;
  })();

  const [selectedKey, setSelectedKey] = useState<string>("");

  const [allTranslations, setAllTranslations] = useState<{ [key: string]: Array<Translation> }>({});
  const [translationsForCurrentKey, setTranslationsForCurrentKey] = useState<{ [key: string]: string | null } | null>(
    null,
  );
  const [keysInDropdown, setKeysInDropdown] = useState<string[]>([]);

  const [sameTranslationForAllLanguages, setSameTranslationForAllLanguages] = useState<boolean>(false);
  const [forceDifferentTranslationForAllLanguages, setForceDifferentTranslationForAllLanguages] =
    useState<boolean>(false);

  // Variables to keep track of what updates need to be sent to backend and when
  const [timeoutSaveDraftId, setTimeoutSaveDraftId] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [translationsToUpdate, setTranslationsToUpdate] = useState<TranslationsToUpdate>({});

  const { mutate: mutateUpdateTranslations } = useMutation({
    mutationFn: async ({ translationsToUpdate }: { translationsToUpdate: TranslationsToUpdate }) => {
      await updateTranslations({ translationsToUpdate });
    },
    onSuccess: async () => {
      setTranslationsToUpdate({});
      // Make sure the preview is rerendered
      queryClient.invalidateQueries({
        queryKey: ["templateRender", selectedPath!.namespace, selectedPath!.name, selectedVersion],
      });
    },
  });

  // Fetch translations for the keys we want to translate
  const dbTranslations = useTranslations(keysInDropdown, selectedVersion);

  // Upon successful retrieval of the translations from backend, update the translations in the state
  useEffect(() => {
    if (dbTranslations.isSuccess && dbTranslations.data) {
      setTranslationsToUpdate({});
      setAllTranslations(dbTranslations.data || {});
    }
  }, [dbTranslations.isSuccess, dbTranslations.data]);

  // If our translations or key changes, fetch the translation that needs to be shown
  useEffect(() => {
    if (!selectedKey) return;

    // Get the translation that is closest to our current template
    const currentTranslation = getTranslationForKey(allTranslations, selectedKey, selectedVersion);
    setTranslationsForCurrentKey(currentTranslation?.value || null);

    // Check if translations are the same for all languages
    if (currentTranslation) {
      const firstValue = Object.values(currentTranslation.value)[0];
      const allSame = Object.values(currentTranslation.value).every((v) => v === firstValue);
      setSameTranslationForAllLanguages(allSame);
    } else {
      setSameTranslationForAllLanguages(false);
    }
  }, [allTranslations, selectedKey]);

  // If we set the keys for our dropdown, define whether our selectedKey is still correct
  useEffect(() => {
    if (!selectedKey || !keysInDropdown.includes(selectedKey)) {
      setSelectedKey(keysInDropdown.length > 0 ? keysInDropdown[0] : "");
    }
  }, [keysInDropdown]);

  // Sort the keys and decide what to show, all or a specific subset
  useEffect(() => {
    setKeysInDropdown(specificKeysToTranslate.length > 0 ? specificKeysToTranslate.sort() : allKeysToTranslate.sort());
  }, [allKeysToTranslate, specificKeysToTranslate]);

  const removeTranslation = async () => {
    const { [selectedKey]: selectedKeyTranslations, ...newAllTranslations } = allTranslations;

    if (
      selectedKeyTranslations.length > 0 &&
      selectedKeyTranslations[0].key === selectedKey &&
      selectedKeyTranslations[0].version === "draft"
    ) {
      selectedKeyTranslations.shift(); // remove the draft version
      newAllTranslations[selectedKey] = selectedKeyTranslations;
      setAllTranslations(newAllTranslations);
    }

    const newUpdateDict = { ...translationsToUpdate, [selectedKey]: null };
    setTranslationsToUpdate(newUpdateDict);

    // Only send updates to the BE if nothing was changed in the past second (to avoid HTTP traffic on each keystroke)
    await mutateUpdateTranslations({
      translationsToUpdate: newUpdateDict,
    });

    setAllTranslations(newAllTranslations);
    setTranslationsToUpdate(newUpdateDict);
  };

  // const createNewTranslation() = () => {
  //   translationsToUpdate[selectedKey] ?? shownTranslation?.value ?? ""
  // }

  const updateTranslation = (
    languageId: string,
    newTranslation: string | null,
    updateAllLanguages: boolean = false,
  ) => {
    // Calculate the current translations for this key. First fetch the other languages from the translations
    // pending to be sent to the backend. Otherwise, fetch it from the shown translation (which is the most relevant
    // translation for the current path).
    const currentTranslation = getTranslationForKey(allTranslations, selectedKey, selectedVersion);
    let valueToUpdate = { ...(currentTranslation?.value || {}), [languageId]: newTranslation };

    if (updateAllLanguages) {
      valueToUpdate = translatableLanguages.reduce(
        (acc, lang) => {
          acc[lang.id] = newTranslation;
          return acc;
        },
        {} as { [key: string]: string | null },
      );
    }

    // Update our allTranslations, so we keep track of the  correct translations without needing
    // to fire backend calls all the time (which causes textarea refreshed and puts the focus back
    // to the start, which is very annoying)
    const { [selectedKey]: selectedKeyTranslations, ...newAllTranslations } = allTranslations;

    let newSelectedKeyTranslations = _.cloneDeep(selectedKeyTranslations);
    if (!getTranslationForCurrentPath("draft")) {
      // If we have no draft translation, copy over from the most relevant translation in the chain
      // and create a draft out of it.

      // Add the new translation highest in the chain as it is most relevant
      const newTranslationObject = {
        key: selectedKey,
        version: "draft",
        value: valueToUpdate,
      } as Translation;

      if (newSelectedKeyTranslations) {
        newSelectedKeyTranslations.unshift(newTranslationObject);
      } else {
        newSelectedKeyTranslations = [newTranslationObject];
      }
    } else {
      // Or update the current translation if it already exists
      newSelectedKeyTranslations[0].value = valueToUpdate;
    }
    newAllTranslations[selectedKey] = newSelectedKeyTranslations;

    const newUpdateDict = { ...translationsToUpdate, [selectedKey]: valueToUpdate };

    // Only send updates to the BE if nothing was changed in the past second (to avoid HTTP traffic on each keystroke)
    if (timeoutSaveDraftId) {
      clearTimeout(timeoutSaveDraftId);
    }
    const timeoutId = setTimeout(async () => {
      await mutateUpdateTranslations({
        translationsToUpdate: stripOuterPFromTranslations(newUpdateDict),
      });
    }, 1000);
    setTimeoutSaveDraftId(timeoutId);

    // setTranslationsForCurrentKey(valueToUpdate);
    setAllTranslations(newAllTranslations);
    setTranslationsToUpdate(newUpdateDict);
  };

  const getTranslationForCurrentPath = (version: string) => {
    const allTranslationsForThisKey = selectedKey in allTranslations ? [...allTranslations[selectedKey]] : [];
    const tmp = allTranslationsForThisKey.find((t) => t.key === selectedKey && t.version === version);
    return tmp;
  };

  return (
    <div>
      {!_.isEqual(allKeysToTranslate, keysInDropdown) && (
        <Alert variant="danger" className="mt-2 text-center position-relative">
          <div>
            This is a filtered list showing you the translatable text.
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id={`tooltip-bottom`}>
                  If you click in the mail previews on the left hand side, you'll see all translations that apply to the
                  text you clicked on.
                </Tooltip>
              }
            >
              <i className="bi bi-info-circle ms-1"></i>
            </OverlayTrigger>
            <br />
            {keysInDropdown.map((key, i) => {
              return (
                <span style={{ fontSize: "0.8em", cursor: "pointer" }} onClick={() => setSelectedKey(key)} key={key}>
                  {key}
                  {i < keysInDropdown.length - 1 ? ", " : ""}
                </span>
              );
            })}
          </div>

          <div className="position-absolute top-0 end-0 p-2 text-nowrap">
            <span style={{ fontSize: "0.6em" }} onClick={() => setKeysInDropdown(allKeysToTranslate)}>
              (<span style={{ cursor: "pointer", textDecoration: "underline" }}>reset</span>)
            </span>
          </div>
        </Alert>
      )}
      <Form.Group as={Row} className="mb-3 align-items-center px-2">
        <Col>
          {keysInDropdown && (
            <>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Form.Select
                  className="border rounded px-3"
                  value={selectedKey || ""}
                  onChange={(e) => {
                    setSelectedKey(e.target.value);
                  }}
                  style={{ marginRight: "8px" }}
                >
                  {[
                    ...new Set(
                      keysInDropdown.sort().map((key) => {
                        return (
                          <option key={key} value={key}>
                            {key}
                          </option>
                        );
                      }),
                    ),
                  ]}
                </Form.Select>
              </div>
            </>
          )}
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mt-2 px-4">
        <Form.Check
          type="checkbox"
          id="forceDifferentTranslationForAllLanguages"
          disabled={selectedVersion !== "draft"}
          label="Same translation for all languages."
          checked={(sameTranslationForAllLanguages && !forceDifferentTranslationForAllLanguages) || false}
          onChange={(e) => {
            setForceDifferentTranslationForAllLanguages(!e.target.checked);
            setSameTranslationForAllLanguages(e.target.checked);
            updateTranslation(
              translatableLanguages[0].id,
              translationsForCurrentKey?.[translatableLanguages[0].id] ||
              getTranslationForCurrentPath("live")?.value[translatableLanguages[0].id] ||
              "...",
              true,
            );
          }}
        />

        {getTranslationForCurrentPath("draft") && (
          <Form.Check
            type="checkbox"
            id="removeCurrentDraftTranslation"
            disabled={selectedVersion !== "draft"}
            label="Remove the current draft translation."
            onChange={() => {
              removeTranslation();
            }}
          />
        )}
      </Form.Group>

      {selectedKey &&
        translatableLanguages.map((l) => {
          const onlyShowOneEditor = sameTranslationForAllLanguages && !forceDifferentTranslationForAllLanguages;
          const shouldDisplay = l.id === translatableLanguages[0].id || !onlyShowOneEditor;

          return (
            <Container key={l.id} style={{ display: shouldDisplay ? "block" : "none" }}>
              <Row className="align-items-center mb-2">
                <Col>
                  <div style={{ fontSize: "12px", color: "#9b9fc2", float: "left", paddingTop: "20px" }}>
                    {onlyShowOneEditor ? "All Languages" : l.name}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col>
                  {/* Prefer the translations that are waiting to get sent to backend above shownTranslation */}
                  <TinyEditor
                    value={translationsForCurrentKey?.[l.id] ?? ""}
                    placeholder=""
                    disabled={selectedVersion !== "draft"}
                    onChange={(txt) => {
                      if (true) {
                        updateTranslation(
                          l.id,
                          txt,
                          sameTranslationForAllLanguages && !forceDifferentTranslationForAllLanguages,
                        );
                      }
                    }}
                  />
                </Col>
              </Row>
            </Container>
          );
        })}
    </div>
  );
}

export default TranslationsAllLanguages;
